import {
  createServerSession,
  fetchLatestCompleteSession,
  writeServerSession,
} from '@/entities/session/api/sessionDAL';
import type { FunnelSession, SavedAs } from '@/entities/session/model/types';

export type SaveOutcome =
  | { kind: 'saved'; sessionId: string; savedAs: SavedAs }
  | { kind: 'start-over' }
  | { kind: 'unchanged' };

async function create(
  session: FunnelSession,
  savedAs: SavedAs,
  funnelConfigVersion: string,
): Promise<SaveOutcome> {
  const status = session.status === 'complete' ? 'complete' : 'incomplete';
  const { result, sessionId } = await createServerSession(
    session.answers,
    status,
    funnelConfigVersion,
  );

  if (result === 'version-mismatch') return { kind: 'start-over' };
  if (result !== 'ok') return { kind: 'unchanged' };
  if (sessionId === null) {
    throw new Error('Session create reported ok without a session id');
  }
  return { kind: 'saved', sessionId, savedAs };
}

export async function save(
  session: FunnelSession,
  savedAs: SavedAs,
  funnelConfigVersion: string,
): Promise<SaveOutcome> {
  if (session.sessionId === null) return create(session, savedAs, funnelConfigVersion);

  const result = await writeServerSession(session.sessionId, session.answers, {
    status: session.status === 'complete' ? 'complete' : 'incomplete',
    funnelConfigVersion,
  });

  switch (result) {
    case 'ok':
    case 'already-complete':
      return { kind: 'saved', sessionId: session.sessionId, savedAs };
    case 'not-found':
    case 'invalid-token':
      return create(session, savedAs, funnelConfigVersion);
    case 'version-mismatch':
      return { kind: 'start-over' };
    case 'error':
      return { kind: 'unchanged' };
  }
}

/**
 * A recovered funnel session has no `sessionId` but is `savedAs: 'authenticated'`: the login
 * rule leaves it alone, and the next answer starts a new server session.
 */
export async function fetchLastCompleteSession(): Promise<FunnelSession | null> {
  const answers = await fetchLatestCompleteSession();
  if (answers === null) return null;
  return { answers, status: 'complete', sessionId: null, savedAs: 'authenticated' };
}
